import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import capitalize from 'lodash/capitalize';
import SelectableProvider from 'core/context/selectable';
import ProviderSearchContent from 'pages/Dashboard/components/ReferringProvider/ProviderSearchContent';
import Dialog from 'pages/Dashboard/components/Dialog';
import ToolTip from 'core/components/Tooltip';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  NPISearchItemDTO,
  ProviderEntryDTO,
} from 'dtos';

const options = ['search', 'manual'];
export const modeOptions = options.map(
  (key) => ({ key, label: capitalize(key) }),
);

export type Suggestion = {
  id: string;
  label: string;
  subtitle?: string | null;
  badge?: string;
  badges?: string[];
  npi?: string | null;
  state?: string | null;
  disabled?: boolean;
  hint?: string;
  onSelect: () => void;
};

type SuggestionListProps = {
  suggestions: Suggestion[];
  currentNpi?: string | null;
  selectedSuggestionNpi: string | null;
  selectedSearchNpi: string | null;
  onSelect: (suggestion: Suggestion) => void;
};

function normalizeSuggestions(suggestions: Suggestion[]) {
  const suggestionsByNpi = new Map<string, Suggestion>();

  suggestions.forEach((suggestion) => {
    const npi = suggestion.npi!;
    const current = suggestionsByNpi.get(npi);
    if (isNil(current)) {
      suggestionsByNpi.set(npi, {
        ...suggestion,
        badges: suggestion.badges ?? (!isNil(suggestion.badge) ? [suggestion.badge] : []),
      });
      return;
    }

    const badges = [
      ...(current.badges ?? (!isNil(current.badge) ? [current.badge] : [])),
      ...(suggestion.badges ?? (!isNil(suggestion.badge) ? [suggestion.badge] : [])),
    ].filter((badge, index, allBadges) => allBadges.indexOf(badge) === index);

    suggestionsByNpi.set(npi, {
      ...current,
      badges,
      disabled: Boolean(current.disabled && suggestion.disabled),
    });
  });

  return Array.from(suggestionsByNpi.values());
}

function SuggestionList({
  suggestions,
  currentNpi,
  selectedSuggestionNpi,
  selectedSearchNpi,
  onSelect,
}: SuggestionListProps) {
  return (
    <div className="grid min-w-0 grid-cols-6 gap-2">
      <h3 className="col-span-6 text-sm font-semibold text-gray-600">
        Suggestions
      </h3>
      {suggestions.map((suggestion, index) => {
        const isCurrent = Boolean(suggestion.npi && suggestion.npi === currentNpi);
        const isCurrentSelection = isCurrent && (
          suggestion.npi === selectedSuggestionNpi
          || (isNil(selectedSuggestionNpi) && isNil(selectedSearchNpi))
        );
        const isSelected = suggestion.npi === selectedSuggestionNpi
          || isCurrentSelection;
        const disabled = suggestion.disabled || isCurrentSelection;
        const badges = suggestion.badges ?? (!isNil(suggestion.badge) ? [suggestion.badge] : []);
        const providerDetails = `NPI ${suggestion.npi} • ${suggestion.state ?? DEFAULT_EMPTY_VALUE}`;
        const rowStart = Math.floor(index / 3) * 3;
        const itemsInRow = Math.min(3, suggestions.length - rowStart);
        const columnCount = 6 / itemsInRow;

        return (
          <div key={suggestion.id} className={`col-span-${columnCount}`}>
            <ToolTip
              title={suggestion.hint}
              hide={!suggestion.disabled || isEmptyString(suggestion.hint)}
            >
              <div>
                <button
                  type="button"
                  className={clsx('flex min-w-0 w-full items-center justify-between rounded border px-2.5 py-2 text-left', {
                    'border-blue-500 bg-blue-50 ring-1 ring-blue-500': isSelected,
                    'border-gray-200 bg-gray-50 text-gray-400': disabled && !isSelected,
                    'border-gray-200 hover:border-blue-300 hover:bg-blue-50/40': !disabled && !isSelected,
                  })}
                  aria-pressed={isSelected}
                  disabled={disabled}
                  onClick={() => { onSelect(suggestion); }}
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    {badges.length > 0 && (
                      <div className="flex min-w-0 flex-wrap gap-1">
                        {badges.map((badge) => (
                          <span
                            key={badge}
                            className={clsx('w-fit rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide', {
                              'bg-gray-100 text-gray-400': disabled && !isSelected,
                              'bg-blue-100 text-blue-600': !disabled || isSelected,
                            })}
                          >
                            {badge}
                          </span>
                        ))}
                      </div>
                    )}
                    <ToolTip title={suggestion.label} auto>
                      <span className={clsx('block min-w-0 truncate text-xs font-semibold', {
                        'text-gray-400': disabled && !isSelected,
                        'text-black': !disabled || isSelected,
                      })}
                      >
                        {suggestion.label}
                      </span>
                    </ToolTip>
                    <span className={clsx('truncate text-xs leading-tight', {
                      'text-gray-400': disabled && !isSelected,
                      'text-gray-600': !disabled || isSelected,
                    })}
                    >
                      {providerDetails}
                    </span>
                  </div>
                  {isSelected && (
                    <CheckCircleIcon className="!h-4 !w-4 shrink-0 text-blue-600" />
                  )}
                </button>
              </div>
            </ToolTip>
          </div>
        );
      })}
    </div>
  );
}

type TitleProps = {
  active: number;
  onChange: (index: number) => void;
  text?: string;
};

function Title({
  text = 'Referring Provider',
}: TitleProps) {
  return (
    <div className="flex flex-1 flex-col gap-2 px-4 pt-4">
      <h2 className="text-lg font-semibold">
        {text}
      </h2>
    </div>
  );
}

type Props = {
  open: boolean;
  mode: number;
  text?: string;
  value?: ProviderEntryDTO | null;
  onChange: (value?: NPISearchItemDTO | null) => void;
  onClose: () => void;
  onModeChange: (mode: number) => void;
  hideBackdrop?: boolean;
  dialogHeight?: number;
  suggestions?: Suggestion[];
};

export default function ProviderSearchDialog({
  open,
  mode,
  text = 'Referring Provider',
  value,
  onChange,
  onClose,
  onModeChange,
  hideBackdrop = true,
  dialogHeight,
  suggestions = [],
}: Props) {
  const isManual = mode === 1;
  const currentNpi = value?.npi?.number ?? null;
  const [selectedSuggestionNpi, setSelectedSuggestionNpi] = useState<string | null>(null);
  const [selectedSearchNpi, setSelectedSearchNpi] = useState<string | null>(null);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const groupedSuggestions = useMemo(() => normalizeSuggestions(suggestions), [suggestions]);

  useEffect(() => {
    if (!open) {
      setSelectedSuggestionNpi(null);
      setSelectedSearchNpi(null);
      setSelectionResetKey(0);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const selectedSuggestion = groupedSuggestions.find(({ npi }) => npi === selectedSuggestionNpi);
  const hasPendingSuggestion = !isNil(selectedSuggestion)
    && selectedSuggestion.npi !== currentNpi;

  const handleSuggestionSelect = (suggestion: Suggestion) => {
    setSelectedSuggestionNpi(suggestion.npi ?? null);
    setSelectedSearchNpi(null);
    setSelectionResetKey((key) => key + 1);
  };

  const handleSearchSelectionChange = (provider: NPISearchItemDTO | null) => {
    setSelectedSearchNpi(provider?.npi ?? null);
    if (!isNil(provider)) {
      setSelectedSuggestionNpi(null);
    }
  };

  const handleConfirmSuggestion = () => {
    selectedSuggestion?.onSelect();
    onClose();
  };

  return (
    <Dialog
      open
      title={(
        <Title
          active={mode}
          text={text}
          onChange={onModeChange}
        />
      )}
      onClose={onClose}
      onConfirm={() => {}}
      className="!w-[50rem] !overflow-hidden !p-0 !space-y-3"
      dialogClassName={clsx({ 'hidden-backdrop': hideBackdrop })}
      maxWidth="lg"
      disableEscapeKeyDown
      closeBtnClasses="!top-2 !right-2"
      disableBackdropClick
      minHeight={dialogHeight}
    >
      {groupedSuggestions.length > 0 && (
        <div className="flex flex-col gap-3 px-4">
          <SuggestionList
            suggestions={groupedSuggestions}
            currentNpi={currentNpi}
            selectedSuggestionNpi={selectedSuggestionNpi}
            selectedSearchNpi={selectedSearchNpi}
            onSelect={handleSuggestionSelect}
          />
          <div className="flex items-center gap-3 text-xs font-semibold text-gray-400">
            <div className="h-px flex-1 bg-gray-200" />
            <span>Or</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
        </div>
      )}
      <h3 className="px-4 text-sm font-semibold text-gray-600">Search NPI</h3>
      <SelectableProvider
        isSolo
        selected={!isNil(value?.npi?.number) ? [value?.npi?.number ?? 0] : []}
      >
        <ProviderSearchContent
          value={isNil(value) ? null : {
            npi: value.npi?.number,
            npiType: value.npi?.type,
            firstName: value.firstName,
            lastName: value.lastName,
            organizationName: value.organizationName,
            state: value.state,
          }}
          onChange={onChange}
          onClose={onClose}
          hasSuggestionSelection={hasPendingSuggestion}
          selectionResetKey={selectionResetKey}
          onSearchSelectionChange={handleSearchSelectionChange}
          onConfirmSuggestion={hasPendingSuggestion ? handleConfirmSuggestion : undefined}
          isManual={isManual}
        />
      </SelectableProvider>
    </Dialog>
  );
}
